import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { assessmentApi } from "../../services/assessmentApi";
import {
  Plus, Sparkles, FileUp, Eye, Trash2, Copy, ChevronRight, ChevronDown,
  GripVertical, AlertTriangle, Check, Archive, Pencil, Shield, Upload, X,
} from "lucide-react";

const PILLAR_LABELS = {
  business_strategy: "Business & AI Strategy Alignment",
  process_readiness: "Process & Use-Case Readiness",
  technology_data: "Technology & Data Foundation",
  organizational_readiness: "Organizational Readiness & Change",
  governance_security: "Governance, Security & Responsible AI",
  measurement_value: "Measurement & Value Realization",
};

const PILLAR_COLORS = {
  business_strategy: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  process_readiness: "bg-[#fef3c7] text-amber-300 border-[#007a9e]/30 dark:border-amber-500/30",
  technology_data: "bg-[#d1fae5] text-emerald-300 border-emerald-500/30",
  organizational_readiness: "bg-purple-500/20 text-purple-300 border-purple-500/30",
  governance_security: "bg-[#fee2e2] text-red-300 border-red-500/30",
  measurement_value: "bg-cyan-500/20 text-cyan-300 border-cyan-500/30",
};

const RESPONSE_TYPES = [
  { value: "single_select", label: "Single Select" },
  { value: "multi_select", label: "Multi Select" },
  { value: "numeric_scale", label: "Numeric Scale" },
  { value: "short_text", label: "Short Text" },
  { value: "long_text", label: "Long Text" },
];

const STATUS_STYLES = {
  draft: "bg-[#007a9e]/20 dark:bg-yellow-500/20 text-yellow-300 border-[#007a9e]/30 dark:border-yellow-500/30",
  published: "bg-[#d1fae5] text-emerald-300 border-emerald-500/30",
  archived: "bg-gray-500/20 text-gray-400 border-gray-500/30",
};

/* ─── Template List View ────────────────────────────────────────── */

const TemplateListView = ({ session, onSelectTemplate }) => {
  const [templates, setTemplates] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [filterIndustry, setFilterIndustry] = useState("");
  const [filterType, setFilterType] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [showAddIndustry, setShowAddIndustry] = useState(false);
  const [newIndustry, setNewIndustry] = useState({ key: "", label: "" });
  const [newTemplate, setNewTemplate] = useState({ industry: "", type: "initial", title: "", description: "" });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [tmpl, ind] = await Promise.all([
        assessmentApi.listTemplates({ industry: filterIndustry, type: filterType }, session),
        assessmentApi.listIndustries(session),
      ]);
      setTemplates(tmpl);
      setIndustries(ind);
    } catch (e) {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, [session, filterIndustry, filterType]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newTemplate.industry || !newTemplate.title) return toast.error("Industry and title required");
    try {
      const created = await assessmentApi.createTemplate(newTemplate, session);
      toast.success("Template created");
      setShowCreate(false);
      setNewTemplate({ industry: "", type: "initial", title: "", description: "" });
      onSelectTemplate(created.id);
    } catch (e) {
      toast.error("Failed to create template");
    }
  };

  const handleDuplicate = async (templateId) => {
    const target = prompt("Enter target industry key (e.g., construction):");
    if (!target) return;
    try {
      const dup = await assessmentApi.duplicateTemplate(templateId, { source_template_id: templateId, target_industry: target }, session);
      toast.success(`Duplicated to ${target}`);
      onSelectTemplate(dup.id);
    } catch (e) {
      toast.error("Failed to duplicate");
    }
  };

  const handleAddIndustry = async () => {
    if (!newIndustry.key || !newIndustry.label) return toast.error("Key and label required");
    try {
      await assessmentApi.addIndustry(newIndustry, session);
      toast.success("Industry added");
      setShowAddIndustry(false);
      setNewIndustry({ key: "", label: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add industry");
    }
  };

  return (
    <div className="space-y-6" data-testid="template-list-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#111827] tracking-tight">Assessment Builder</h2>
          <p className="text-sm text-[#6b7280] mt-1">Create and manage industry-specific AI readiness assessments</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowAddIndustry(true)} className="h-9 rounded-none border border-[#e5e7eb] bg-white text-[#111827] hover:bg-[#f9fafb]" data-testid="add-industry-btn">
            <Plus className="mr-2 h-4 w-4" /> Add Industry
          </Button>
          <Button onClick={() => setShowCreate(true)} className="h-9 rounded-none bg-[#1a56db] text-white hover:bg-[#1a56db]/90" data-testid="create-template-btn">
            <Plus className="mr-2 h-4 w-4" /> New Template
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <select value={filterIndustry} onChange={(e) => setFilterIndustry(e.target.value)}
          className="h-9 rounded-none border border-[#e5e7eb] bg-muted text-[#111827] text-sm px-3" data-testid="filter-industry">
          <option value="">All Industries</option>
          {industries.map((i) => <option key={i.key} value={i.key}>{i.label}</option>)}
        </select>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
          className="h-9 rounded-none border border-[#e5e7eb] bg-muted text-[#111827] text-sm px-3" data-testid="filter-type">
          <option value="">All Types</option>
          <option value="initial">Initial (Free)</option>
          <option value="detailed">Detailed (Paid)</option>
        </select>
      </div>

      {/* Add Industry Modal */}
      {showAddIndustry && (
        <Card className="border border-[#e5e7eb] bg-background/60">
          <CardContent className="p-4 space-y-3">
            <div className="text-sm font-medium text-foreground">Add New Industry</div>
            <input placeholder="Key (e.g., real_estate)" value={newIndustry.key}
              onChange={(e) => setNewIndustry({ ...newIndustry, key: e.target.value.toLowerCase().replace(/\s/g, "_") })}
              className="w-full h-9 rounded-none border border-[#e5e7eb] bg-muted text-[#111827] text-sm px-3" />
            <input placeholder="Label (e.g., Real Estate)" value={newIndustry.label}
              onChange={(e) => setNewIndustry({ ...newIndustry, label: e.target.value })}
              className="w-full h-9 rounded-none border border-[#e5e7eb] bg-muted text-[#111827] text-sm px-3" />
            <div className="flex gap-2">
              <Button onClick={handleAddIndustry} className="h-8 rounded-none bg-[#1a56db] text-white text-xs">Add</Button>
              <Button onClick={() => setShowAddIndustry(false)} className="h-8 rounded-none border border-[#e5e7eb] bg-white text-[#111827] text-xs">Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Template Modal */}
      {showCreate && (
        <Card className="border border-[#e5e7eb] bg-background/60">
          <CardContent className="p-4 space-y-3">
            <div className="text-sm font-medium text-foreground">Create New Template</div>
            <select value={newTemplate.industry} onChange={(e) => setNewTemplate({ ...newTemplate, industry: e.target.value })}
              className="w-full h-9 rounded-none border border-[#e5e7eb] bg-muted text-[#111827] text-sm px-3">
              <option value="">Select Industry</option>
              {industries.map((i) => <option key={i.key} value={i.key}>{i.label}</option>)}
            </select>
            <select value={newTemplate.type} onChange={(e) => setNewTemplate({ ...newTemplate, type: e.target.value })}
              className="w-full h-9 rounded-none border border-[#e5e7eb] bg-muted text-[#111827] text-sm px-3">
              <option value="initial">Initial (Free)</option>
              <option value="detailed">Detailed (Paid)</option>
            </select>
            <input placeholder="Template Title" value={newTemplate.title}
              onChange={(e) => setNewTemplate({ ...newTemplate, title: e.target.value })}
              className="w-full h-9 rounded-none border border-[#e5e7eb] bg-muted text-[#111827] text-sm px-3" />
            <textarea placeholder="Description (optional)" value={newTemplate.description}
              onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
              className="w-full h-20 rounded-none border border-[#e5e7eb] bg-muted text-[#111827] text-sm px-3 py-2 resize-none" />
            <div className="flex gap-2">
              <Button onClick={handleCreate} className="h-8 rounded-none bg-[#1a56db] text-white text-xs">Create</Button>
              <Button onClick={() => setShowCreate(false)} className="h-8 rounded-none border border-[#e5e7eb] bg-white text-[#111827] text-xs">Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Template Grid */}
      {loading ? (
        <div className="text-center text-[#6b7280] py-12">Loading templates...</div>
      ) : templates.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-[#e5e7eb] rounded-xl">
          <p className="text-[#6b7280]">No templates yet. Create your first assessment template to get started.</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {templates.map((t) => (
            <Card key={t.id} className="group border border-[#e5e7eb] bg-muted hover:border-[#e5e7eb] cursor-pointer transition-all"
              onClick={() => onSelectTemplate(t.id)} data-testid={`template-card-${t.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-[#111827] truncate">{t.title}</h3>
                    <p className="text-xs text-[#9ca3af] mt-0.5">{t.template_id} v{t.version}</p>
                  </div>
                  <Badge className={`ml-2 text-[10px] rounded-none ${STATUS_STYLES[t.status] || ""}`}>{t.status}</Badge>
                </div>
                <div className="flex items-center gap-2 text-xs text-[#9ca3af]">
                  <span className="capitalize">{t.industry?.replace("_", " ")}</span>
                  <span>|</span>
                  <span>{t.type === "initial" ? "Free Initial" : "Paid Detailed"}</span>
                  <span>|</span>
                  <span>{t.question_count || 0} questions</span>
                </div>
                {t.description && <p className="text-xs text-[#9ca3af] mt-2 line-clamp-2">{t.description}</p>}
                <div className="flex gap-1 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button size="sm" className="h-6 rounded-none text-[10px] border border-[#e5e7eb] bg-white text-foreground"
                    onClick={(e) => { e.stopPropagation(); handleDuplicate(t.id); }}>
                    <Copy className="h-3 w-3 mr-1" /> Duplicate
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};


/* ─── Question Editor ───────────────────────────────────────────── */

const QuestionEditor = ({ question, onSave, onCancel, pillars }) => {
  const [q, setQ] = useState({
    question_text: "",
    help_text: "",
    response_type: "single_select",
    answer_options: [
      { label: "Not Started", value: "0", score: 0 },
      { label: "Aware", value: "1", score: 1 },
      { label: "Defined", value: "2", score: 2 },
      { label: "Operational", value: "3", score: 3 },
      { label: "Optimized", value: "4", score: 4 },
    ],
    pillar: "",
    sub_pillar: "",
    category_tag: "",
    weight: 1.0,
    required: true,
    evidence_required: false,
    risk_flag: false,
    opportunity_flag: false,
    ...question,
  });

  const updateOption = (idx, field, value) => {
    const opts = [...q.answer_options];
    opts[idx] = { ...opts[idx], [field]: field === "score" ? parseFloat(value) || 0 : value };
    setQ({ ...q, answer_options: opts });
  };
  const addOption = () => setQ({ ...q, answer_options: [...q.answer_options, { label: "", value: "", score: 0 }] });
  const removeOption = (idx) => setQ({ ...q, answer_options: q.answer_options.filter((_, i) => i !== idx) });

  return (
    <div className="space-y-4 border border-[#e5e7eb] bg-muted p-4 rounded-lg" data-testid="question-editor">
      <div>
        <label className="block text-xs text-[#6b7280] mb-1">Question Text *</label>
        <textarea value={q.question_text} onChange={(e) => setQ({ ...q, question_text: e.target.value })}
          className="w-full h-20 rounded-none border border-[#e5e7eb] bg-muted text-[#111827] text-sm px-3 py-2 resize-none" />
      </div>
      <div>
        <label className="block text-xs text-[#6b7280] mb-1">Help Text / Tooltip</label>
        <input value={q.help_text} onChange={(e) => setQ({ ...q, help_text: e.target.value })}
          className="w-full h-9 rounded-none border border-[#e5e7eb] bg-muted text-[#111827] text-sm px-3" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs text-[#6b7280] mb-1">Pillar</label>
          <select value={q.pillar} onChange={(e) => setQ({ ...q, pillar: e.target.value })}
            className="w-full h-9 rounded-none border border-[#e5e7eb] bg-muted text-[#111827] text-xs px-2">
            <option value="">Select Pillar</option>
            {Object.entries(pillars).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-[#6b7280] mb-1">Sub-Pillar</label>
          <input value={q.sub_pillar} onChange={(e) => setQ({ ...q, sub_pillar: e.target.value })}
            className="w-full h-9 rounded-none border border-[#e5e7eb] bg-muted text-[#111827] text-xs px-2" />
        </div>
        <div>
          <label className="block text-xs text-[#6b7280] mb-1">Response Type</label>
          <select value={q.response_type} onChange={(e) => setQ({ ...q, response_type: e.target.value })}
            className="w-full h-9 rounded-none border border-[#e5e7eb] bg-muted text-[#111827] text-xs px-2">
            {RESPONSE_TYPES.map((rt) => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-[#6b7280] mb-1">Weight</label>
          <input type="number" step="0.1" min="0" value={q.weight}
            onChange={(e) => setQ({ ...q, weight: parseFloat(e.target.value) || 1 })}
            className="w-full h-9 rounded-none border border-[#e5e7eb] bg-muted text-[#111827] text-xs px-2" />
        </div>
      </div>

      <div>
        <label className="block text-xs text-[#6b7280] mb-1">Category Tag</label>
        <input value={q.category_tag} onChange={(e) => setQ({ ...q, category_tag: e.target.value })}
          className="w-full h-9 rounded-none border border-[#e5e7eb] bg-muted text-[#111827] text-sm px-3" />
      </div>

      {/* Answer Options */}
      {(q.response_type === "single_select" || q.response_type === "multi_select") && (
        <div>
          <label className="block text-xs text-[#6b7280] mb-2">Answer Options & Score Map</label>
          <div className="space-y-2">
            {q.answer_options.map((opt, idx) => (
              <div key={idx} className="flex gap-2 items-center">
                <input placeholder="Label" value={opt.label} onChange={(e) => updateOption(idx, "label", e.target.value)}
                  className="flex-1 h-8 rounded-none border border-[#e5e7eb] bg-muted text-[#111827] text-xs px-2" />
                <input placeholder="Value" value={opt.value} onChange={(e) => updateOption(idx, "value", e.target.value)}
                  className="w-24 h-8 rounded-none border border-[#e5e7eb] bg-muted text-[#111827] text-xs px-2" />
                <input type="number" step="0.5" placeholder="Score" value={opt.score}
                  onChange={(e) => updateOption(idx, "score", e.target.value)}
                  className="w-20 h-8 rounded-none border border-[#e5e7eb] bg-muted text-[#111827] text-xs px-2" />
                <button onClick={() => removeOption(idx)} className="text-[#ef4444] hover:text-red-300"><X className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <Button size="sm" onClick={addOption} className="h-7 mt-2 rounded-none text-[10px] border border-[#e5e7eb] bg-white text-foreground">
            <Plus className="h-3 w-3 mr-1" /> Add Option
          </Button>
        </div>
      )}

      {/* Flags */}
      <div className="flex flex-wrap gap-4">
        {[
          ["required", "Required"],
          ["evidence_required", "Evidence Required"],
          ["risk_flag", "Risk Flag"],
          ["opportunity_flag", "Opportunity Question"],
        ].map(([key, label]) => (
          <label key={key} className="flex items-center gap-2 text-xs text-[#4b5563] cursor-pointer">
            <input type="checkbox" checked={q[key]} onChange={(e) => setQ({ ...q, [key]: e.target.checked })}
              className="rounded border-[#e5e7eb]" />
            {label}
          </label>
        ))}
      </div>

      <div className="flex gap-2 pt-2">
        <Button onClick={() => onSave(q)} className="h-8 rounded-none bg-[#1a56db] text-white text-xs" data-testid="save-question-btn">
          <Check className="h-3 w-3 mr-1" /> Save Question
        </Button>
        <Button onClick={onCancel} className="h-8 rounded-none border border-[#e5e7eb] bg-white text-[#111827] text-xs">Cancel</Button>
      </div>
    </div>
  );
};


/* ─── Template Detail / Builder View ────────────────────────────── */

const TemplateDetailView = ({ session, templateId, onBack, onStartAssessment, onViewResult }) => {
  const [template, setTemplate] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [showAIGenerate, setShowAIGenerate] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  const [bulkCsv, setBulkCsv] = useState("");
  const [aiOpts, setAiOpts] = useState({ context_prompt: "", target_audience: "", regulatory_context: "", question_count: 15 });
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [tmpl, qs, cov] = await Promise.all([
        assessmentApi.getTemplate(templateId, session),
        assessmentApi.listQuestions(templateId, session),
        assessmentApi.checkCoverage(templateId, session),
      ]);
      setTemplate(tmpl);
      setQuestions(qs);
      setCoverage(cov);
    } catch (e) {
      toast.error("Failed to load template");
    } finally {
      setLoading(false);
    }
  }, [templateId, session]);

  useEffect(() => { load(); }, [load]);

  const handleSaveQuestion = async (q) => {
    try {
      const payload = { ...q };
      delete payload.id;
      delete payload.tenant_id;
      delete payload.template_id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.rationale;

      if (editingQuestion) {
        await assessmentApi.updateQuestion(templateId, editingQuestion.id, payload, session);
        toast.success("Question updated");
      } else {
        payload.display_order = questions.length;
        await assessmentApi.addQuestion(templateId, payload, session);
        toast.success("Question added");
      }
      setEditingQuestion(null);
      setShowAddQuestion(false);
      load();
    } catch (e) {
      toast.error("Failed to save question");
    }
  };

  const handleDeleteQuestion = async (qId) => {
    if (!window.confirm("Delete this question?")) return;
    try {
      await assessmentApi.deleteQuestion(templateId, qId, session);
      toast.success("Question deleted");
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const handleStatusChange = async (status) => {
    if (status === "published" && !window.confirm("Publish this template? It will become active for assessments.")) return;
    try {
      await assessmentApi.updateTemplate(templateId, { status }, session);
      toast.success(`Template ${status}`);
      load();
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const handleAIGenerate = async () => {
    setGenerating(true);
    try {
      const result = await assessmentApi.generateAI(templateId, {
        industry: template.industry,
        assessment_type: template.type,
        ...aiOpts,
      }, session);
      toast.success(`Generated ${result.generated} questions`);
      setShowAIGenerate(false);
      load();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "";
      if (status === 504 || detail.includes("timed out")) {
        toast.error("AI generation timed out", { description: "The AI service was slow. Please try again — it usually works on retry.", duration: 8000 });
      } else if (status === 502 || detail.includes("502")) {
        toast.error("AI service temporarily unavailable", { description: "Please wait a moment and try again.", duration: 8000 });
      } else {
        toast.error("AI generation failed", { description: detail || "Please try again.", duration: 8000 });
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleBulkImport = async () => {
    if (!bulkCsv.trim()) return toast.error("Paste CSV content");
    try {
      const result = await assessmentApi.bulkImport(templateId, bulkCsv, session);
      toast.success(`Imported ${result.imported} questions`);
      setShowBulkImport(false);
      setBulkCsv("");
      load();
    } catch (e) {
      toast.error("Import failed");
    }
  };

  if (loading) return <div className="text-center text-[#6b7280] py-12">Loading template...</div>;
  if (!template) return <div className="text-center text-[#6b7280] py-12">Template not found</div>;

  return (
    <div className="space-y-6" data-testid="template-detail-view">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <button onClick={onBack} className="text-xs text-[#9ca3af] hover:text-[#374151] mb-2 flex items-center gap-1" data-testid="back-to-list-btn">
            &larr; Back to Templates
          </button>
          <h2 className="text-xl font-bold text-foreground">{template.title}</h2>
          <div className="flex items-center gap-2 mt-1">
            <Badge className={`text-[10px] rounded-none ${STATUS_STYLES[template.status]}`}>{template.status}</Badge>
            <span className="text-xs text-[#9ca3af]">v{template.version}</span>
            <span className="text-xs text-[#9ca3af]">|</span>
            <span className="text-xs text-[#9ca3af] capitalize">{template.industry?.replace("_", " ")}</span>
            <span className="text-xs text-[#9ca3af]">|</span>
            <span className="text-xs text-[#9ca3af]">{template.type === "initial" ? "Free Initial" : "Paid Detailed"}</span>
          </div>
        </div>
        <div className="flex gap-2">
          {template.status === "draft" && (
            <Button onClick={() => handleStatusChange("published")}
              className="h-8 rounded-none bg-emerald-600 text-[#111827] text-xs hover:bg-emerald-700" data-testid="publish-btn">
              <Check className="h-3 w-3 mr-1" /> Publish
            </Button>
          )}
          {template.status === "published" && (
            <Button onClick={() => handleStatusChange("archived")}
              className="h-8 rounded-none border border-[#e5e7eb] bg-white text-[#111827] text-xs" data-testid="archive-btn">
              <Archive className="h-3 w-3 mr-1" /> Archive
            </Button>
          )}
          {template.status === "archived" && (
            <Button onClick={() => handleStatusChange("draft")}
              className="h-8 rounded-none border border-[#e5e7eb] bg-white text-[#111827] text-xs">
              Reopen as Draft
            </Button>
          )}
        </div>
      </div>

      {/* Coverage Checker */}
      {coverage && (
        <Card className="border border-[#e5e7eb] bg-muted">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-[#6b7280] mb-3">Pillar Coverage</div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {Object.entries(coverage.pillars).map(([key, val]) => (
                <div key={key} className={`p-2 rounded border ${val.has_coverage ? "border-emerald-500/30 bg-[#10b981]/5" : "border-red-500/30 bg-[#ef4444]/5"}`}>
                  <div className="text-[10px] text-[#6b7280] truncate">{val.label}</div>
                  <div className={`text-lg font-bold ${val.has_coverage ? "text-[#10b981]" : "text-[#ef4444]"}`}>{val.count}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-4 mt-3 text-xs text-[#9ca3af]">
              <span>Total: {coverage.total} questions</span>
              <span>Opportunity: {coverage.opportunity_questions}</span>
              {Object.values(coverage.pillars).some((p) => !p.has_coverage) && (
                <span className="text-[#ef4444] flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> Missing pillar coverage
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => { setEditingQuestion(null); setShowAddQuestion(true); }}
          className="h-8 rounded-none bg-[#1a56db] text-white text-xs" data-testid="add-question-btn">
          <Plus className="h-3 w-3 mr-1" /> Add Question
        </Button>
        <Button onClick={() => setShowAIGenerate(true)}
          className="h-8 rounded-none border border-purple-500/30 bg-purple-500/10 text-purple-300 text-xs hover:bg-purple-500/20" data-testid="ai-generate-btn">
          <Sparkles className="h-3 w-3 mr-1" /> Generate with AI
        </Button>
        <Button onClick={() => setShowBulkImport(true)}
          className="h-8 rounded-none border border-[#e5e7eb] bg-white text-[#111827] text-xs" data-testid="bulk-import-btn">
          <Upload className="h-3 w-3 mr-1" /> Bulk Import
        </Button>
        {template.status === "published" && questions.length > 0 && onStartAssessment && (
          <Button onClick={async () => {
            try {
              const sess = await assessmentApi.createSession({ template_id: templateId }, session);
              onStartAssessment(sess.id);
            } catch (e) { toast.error("Failed to start assessment"); }
          }}
            className="h-8 rounded-none border border-emerald-500/30 bg-[#ecfdf5] text-emerald-300 text-xs hover:bg-[#d1fae5]" data-testid="start-assessment-btn">
            <Eye className="h-3 w-3 mr-1" /> Preview Assessment
          </Button>
        )}
        <Button onClick={async () => {
          try {
            const s = await assessmentApi.listSessions({}, session);
            setSessions(s.filter((x) => x.template_id === templateId));
            setShowSessions(!showSessions);
          } catch { toast.error("Failed to load sessions"); }
        }}
          className="h-8 rounded-none border border-[#e5e7eb] bg-white text-[#111827] text-xs">
          <Eye className="h-3 w-3 mr-1" /> Sessions ({sessions.length || "..."})
        </Button>
      </div>

      {/* Sessions List */}
      {showSessions && sessions.length > 0 && (
        <Card className="border border-[#e5e7eb] bg-muted">
          <CardContent className="p-3 space-y-2">
            <div className="text-xs text-[#6b7280] uppercase">Assessment Sessions</div>
            {sessions.map((s) => (
              <div key={s.id} className="flex items-center justify-between p-2 border border-[#e5e7eb] rounded bg-muted/60 text-xs">
                <div>
                  <span className="text-foreground">{s.id.slice(0, 16)}...</span>
                  <span className="text-[#9ca3af] ml-2">{s.status} | {s.progress_percent}%</span>
                </div>
                <div className="flex gap-1">
                  {s.status === "in_progress" && onStartAssessment && (
                    <Button size="sm" onClick={() => onStartAssessment(s.id)}
                      className="h-6 rounded-none text-[10px] bg-[#1a56db] text-white">Resume</Button>
                  )}
                  {s.status === "completed" && onViewResult && (
                    <Button size="sm" onClick={async () => {
                      try {
                        const full = await assessmentApi.getSession(s.id, session);
                        if (full.result) onViewResult(full.result);
                        else toast.error("No results yet");
                      } catch { toast.error("Failed to load result"); }
                    }}
                      className="h-6 rounded-none text-[10px] bg-emerald-600 text-foreground">View Results</Button>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* AI Generate Panel */}
      {showAIGenerate && (
        <Card className="border border-purple-500/20 bg-purple-500/5">
          <CardContent className="p-4 space-y-3">
            <div className="text-sm font-medium text-purple-300 flex items-center gap-2">
              <Sparkles className="h-4 w-4" /> Generate Questions with AI
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-[#6b7280] mb-1">Context Prompt (optional)</label>
                <textarea value={aiOpts.context_prompt} onChange={(e) => setAiOpts({ ...aiOpts, context_prompt: e.target.value })}
                  placeholder="e.g., Focus on patient intake workflows and HIPAA compliance"
                  className="w-full h-20 rounded-none border border-[#e5e7eb] bg-muted text-[#111827] text-xs px-3 py-2 resize-none" />
              </div>
              <div className="space-y-2">
                <div>
                  <label className="block text-xs text-[#6b7280] mb-1">Target Audience</label>
                  <input value={aiOpts.target_audience} onChange={(e) => setAiOpts({ ...aiOpts, target_audience: e.target.value })}
                    placeholder="e.g., C-suite executives, IT directors"
                    className="w-full h-8 rounded-none border border-[#e5e7eb] bg-muted text-[#111827] text-xs px-2" />
                </div>
                <div>
                  <label className="block text-xs text-[#6b7280] mb-1">Regulatory Context</label>
                  <input value={aiOpts.regulatory_context} onChange={(e) => setAiOpts({ ...aiOpts, regulatory_context: e.target.value })}
                    placeholder="e.g., HIPAA, SOX, FERPA"
                    className="w-full h-8 rounded-none border border-[#e5e7eb] bg-muted text-[#111827] text-xs px-2" />
                </div>
                <div>
                  <label className="block text-xs text-[#6b7280] mb-1">Question Count</label>
                  <input type="number" value={aiOpts.question_count} onChange={(e) => setAiOpts({ ...aiOpts, question_count: parseInt(e.target.value) || 15 })}
                    className="w-full h-8 rounded-none border border-[#e5e7eb] bg-muted text-[#111827] text-xs px-2" />
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleAIGenerate} disabled={generating}
                className="h-8 rounded-none bg-purple-600 text-[#111827] text-xs hover:bg-purple-700" data-testid="generate-ai-submit">
                {generating ? "Generating (this may take 30-60s)..." : "Generate Questions"}
              </Button>
              <Button onClick={() => setShowAIGenerate(false)} className="h-8 rounded-none border border-[#e5e7eb] bg-white text-[#111827] text-xs">Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bulk Import Panel */}
      {showBulkImport && (
        <Card className="border border-[#e5e7eb] bg-muted">
          <CardContent className="p-4 space-y-3">
            <div className="text-sm font-medium text-foreground">Bulk Import (CSV)</div>
            <p className="text-xs text-[#9ca3af]">Headers: question_text, help_text, response_type, answer_options (pipe-separated), scores (pipe-separated), pillar, sub_pillar, category_tag, weight, required, evidence_required, risk_flag, opportunity_flag</p>
            <textarea value={bulkCsv} onChange={(e) => setBulkCsv(e.target.value)}
              placeholder="Paste CSV content here..."
              className="w-full h-32 rounded-none border border-[#e5e7eb] bg-muted text-[#111827] text-xs font-mono px-3 py-2 resize-none" />
            <div className="flex gap-2">
              <Button onClick={handleBulkImport} className="h-8 rounded-none bg-[#1a56db] text-white text-xs">Import</Button>
              <Button onClick={() => setShowBulkImport(false)} className="h-8 rounded-none border border-[#e5e7eb] bg-white text-[#111827] text-xs">Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Question */}
      {(showAddQuestion || editingQuestion) && (
        <QuestionEditor
          question={editingQuestion || {}}
          onSave={handleSaveQuestion}
          onCancel={() => { setShowAddQuestion(false); setEditingQuestion(null); }}
          pillars={PILLAR_LABELS}
        />
      )}

      {/* Questions List */}
      <div className="space-y-2">
        <div className="text-xs uppercase tracking-wide text-[#6b7280] mb-2">Questions ({questions.length})</div>
        {questions.length === 0 ? (
          <div className="text-center py-8 border border-dashed border-[#e5e7eb] rounded-lg">
            <p className="text-[#9ca3af] text-sm">No questions yet. Add manually, generate with AI, or bulk import.</p>
          </div>
        ) : (
          questions.map((q, idx) => (
            <div key={q.id} className="flex items-start gap-3 p-3 border border-[#e5e7eb] bg-muted/60 rounded-lg group hover:border-[#e5e7eb] transition-all" data-testid={`question-row-${q.id}`}>
              <div className="text-[#d1d5db] pt-1 cursor-grab"><GripVertical className="h-4 w-4" /></div>
              <div className="text-xs text-[#9ca3af] pt-1 w-6">{idx + 1}.</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground">{q.question_text}</p>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {q.pillar && (
                    <Badge className={`text-[9px] rounded-none ${PILLAR_COLORS[q.pillar] || "bg-[#f9fafb] text-[#6b7280]"}`}>
                      {PILLAR_LABELS[q.pillar] || q.pillar}
                    </Badge>
                  )}
                  {q.opportunity_flag && <Badge className="text-[9px] rounded-none bg-[#fef3c7] text-amber-300 border-[#007a9e]/30 dark:border-amber-500/30">Opportunity</Badge>}
                  {q.risk_flag && <Badge className="text-[9px] rounded-none bg-[#fee2e2] text-red-300 border-red-500/30">Risk</Badge>}
                  {q.evidence_required && <Badge className="text-[9px] rounded-none bg-cyan-500/20 text-cyan-300 border-cyan-500/30">Evidence</Badge>}
                  <span className="text-[9px] text-[#9ca3af]">w:{q.weight} | {q.response_type} | {q.answer_options?.length || 0} opts</span>
                </div>
                {q.rationale && <p className="text-[10px] text-[#d1d5db] mt-1 italic">{q.rationale}</p>}
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => { setShowAddQuestion(false); setEditingQuestion(q); }}
                  className="p-1.5 text-[#9ca3af] hover:text-foreground" data-testid={`edit-question-${q.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => handleDeleteQuestion(q.id)}
                  className="p-1.5 text-[#ef4444]/50 hover:text-[#ef4444]" data-testid={`delete-question-${q.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};


/* ─── Main Assessment Builder ───────────────────────────────────── */

const AssessmentBuilder = ({ session, onStartAssessment, onViewResult }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  if (selectedTemplate) {
    return (
      <TemplateDetailView
        session={session}
        templateId={selectedTemplate}
        onBack={() => setSelectedTemplate(null)}
        onStartAssessment={onStartAssessment}
        onViewResult={onViewResult}
      />
    );
  }

  return (
    <TemplateListView
      session={session}
      onSelectTemplate={setSelectedTemplate}
    />
  );
};

export default AssessmentBuilder;
